'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Briefcase,
  Building2,
  ChevronDown,
  ChevronUp,
  CircleStar,
  DoorOpen,
  GraduationCap,
  Handshake,
  HouseHeart,
  type LucideIcon as LucideIconType,
  Sofa,
  TreePalm,
} from 'lucide-react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import * as styles from './quickFacts.styles';

type Fact = {
  id: string;
  icon: LucideIconType;
  label: string;
};

const PROPERTY_TYPE_LABEL: Record<string, string> = {
  large: 'Apartment building',
  medium: 'Apartment building',
  garden: 'Apartment building',
  townhouse: 'Townhouse',
  divided: 'Duplex',
  condo: 'Condo',
  house: 'House',
  land: 'Land',
  room: 'Room',
};

// Default-collapsed threshold — match the design comp (4 facts visible
// by default; everything else lives behind the toggle).
const COLLAPSED_LIMIT = 4;

type QuickFactsProps = {
  listing: PropertyDetailListing;
};

export function QuickFacts({ listing }: QuickFactsProps) {
  const t = useTranslations('hdp');
  const [expanded, setExpanded] = useState(false);

  const facts = collectFacts(listing);
  if (facts.length === 0) return null;

  const visibleFacts = expanded ? facts : facts.slice(0, COLLAPSED_LIMIT);
  const showToggle = facts.length > COLLAPSED_LIMIT;

  return (
    <aside className={styles.card} data-testid="section-quick-facts">
      <h3 className={styles.heading}>{t('quickFactsTitle')}</h3>
      <div className={styles.grid} data-testid="cluster-listing-meta">
        {visibleFacts.map((fact) => (
          <div key={fact.id} className={styles.factRow} data-testid={`badge-audience-${fact.id}`}>
            <LucideIcon icon={fact.icon} size="sm" className={styles.factIcon} />
            <span className={styles.factLabel}>{fact.label}</span>
          </div>
        ))}
      </div>
      {showToggle && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => setExpanded((v) => !v)}
          data-testid="button-quick-facts-toggle"
        >
          {expanded ? t('viewLess') : t('viewMore')}
          <LucideIcon icon={expanded ? ChevronUp : ChevronDown} size="sm" />
        </button>
      )}
    </aside>
  );
}

function collectFacts(listing: PropertyDetailListing): Array<Fact> {
  const facts: Array<Fact> = [];

  const propertyType = (listing as { propertyType?: string }).propertyType;
  const propertyTypeLabel = propertyType ? PROPERTY_TYPE_LABEL[propertyType] : null;
  if (propertyTypeLabel) {
    facts.push({ id: 'property-type', icon: Building2, label: propertyTypeLabel });
  }

  const details = listing.details as
    | {
        furnished?: boolean;
        studentHousing?: boolean;
        seniorHousing?: boolean;
        roomForRent?: { roomDetails?: unknown };
      }
    | undefined;

  const isFurnished = Boolean(details?.furnished);
  const isStudentHousing =
    (listing as { studentHousing?: boolean }).studentHousing === true || Boolean(details?.studentHousing);
  const isSenior = (listing as { seniorHousing?: boolean }).seniorHousing === true || Boolean(details?.seniorHousing);
  const isMilitary = (listing as { militaryHousing?: boolean }).militaryHousing === true;
  const isIncomeRestricted =
    (listing as { incomeRestricted?: boolean }).incomeRestricted === true ||
    (listing.contact as { restricted?: boolean } | undefined)?.restricted === true;
  const isSublet = (listing as { listingType?: string }).listingType === 'sublet';
  const isRoomForRent = (listing as { listingType?: string }).listingType === 'room';

  if (isFurnished) facts.push({ id: 'furnished', icon: Sofa, label: 'Furnished' });
  if (isStudentHousing) {
    facts.push({ id: 'student-housing', icon: GraduationCap, label: 'Student housing' });
  }
  if (isSublet) facts.push({ id: 'sublet', icon: Handshake, label: 'Sublet' });
  if (isIncomeRestricted) {
    facts.push({ id: 'income-restricted', icon: HouseHeart, label: 'Income-restricted' });
  }
  if (isSenior) facts.push({ id: 'senior-55', icon: TreePalm, label: 'Senior 55+' });
  if (isMilitary) facts.push({ id: 'military-housing', icon: CircleStar, label: 'Military housing' });
  if (isRoomForRent) facts.push({ id: 'room-for-rent', icon: DoorOpen, label: 'Room for rent' });

  // Corporate housing isn't a typed field on the listing yet — placeholder
  // so the collector reads symmetrically with the design comp.
  // if (isCorporateHousing) facts.push({ id: 'corporate-housing', icon: Briefcase, label: 'Corporate housing' });
  void Briefcase;

  return facts;
}
